import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';
import { companyInfo } from '../mock/data';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    aiExperience: '',
    projectType: '',
    budget: '',
    timeline: '',
    challenges: '',
    goals: '',
    currentAI: '',
    teamSize: '',
    message: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare email template parameters
      const templateParams = {
        to_email: 'info@bluemp.biz',
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        company: formData.company,
        phone: formData.phone,
        industry: formData.industry,
        team_size: formData.teamSize,
        ai_experience: formData.aiExperience,
        project_type: formData.projectType,
        timeline: formData.timeline,
        budget: formData.budget,
        current_ai: formData.currentAI,
        challenges: formData.challenges,
        goals: formData.goals,
        message: formData.message,
        submission_date: new Date().toLocaleString(),
        subject: 'New Discovery Call Request from Website'
      };

      // Send email using EmailJS
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      toast.success('Discovery call request submitted successfully! We\'ll contact you within 24 hours.');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        aiExperience: '',
        projectType: '',
        budget: '',
        timeline: '',
        challenges: '',
        goals: '',
        currentAI: '',
        teamSize: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Failed to submit request. Please try again or contact us directly at info@bluemp.biz');
    }
  };

  return (
    <div className="dark-theme pt-24">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="display-large text-white mb-6">
              We're Here to Help You{' '}
              <span className="text-bluemp-400">Succeed with AI</span>
            </h1>
            <p className="body-large text-gray-300 max-w-3xl mx-auto mb-8">
              Every AI journey starts with a conversation. Book a discovery call to discuss your challenges, explore opportunities, and learn how Blue MP can help you achieve your AI goals with integrity.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="display-medium text-white mb-8">Get In Touch</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center">
                    <Phone size={24} className="text-bluemp-400" />
                  </div>
                  <div>
                    <h3 className="heading-3 text-white">Phone</h3>
                    <p className="body-medium text-gray-300">{companyInfo.contact.phone}</p>
                  </div>
                </div>

                <a 
                  href="mailto:info@bluemp.biz?subject=Website%20Enquiry"
                  className="flex items-center gap-4 hover:bg-bluemp-400/10 p-2 rounded-lg transition-colors duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center">
                    <Mail size={24} className="text-bluemp-400" />
                  </div>
                  <div>
                    <h3 className="heading-3 text-white">Email</h3>
                    <p className="body-medium text-gray-300">{companyInfo.contact.email}</p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center">
                    <MapPin size={24} className="text-bluemp-400" />
                  </div>
                  <div>
                    <h3 className="heading-3 text-white">Location</h3>
                    <p className="body-medium text-gray-300">{companyInfo.contact.location}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-bluemp-400/20 rounded-lg flex items-center justify-center">
                    <Clock size={24} className="text-bluemp-400" />
                  </div>
                  <div>
                    <h3 className="heading-3 text-white">Business Hours</h3>
                    <p className="body-medium text-gray-300">Mon-Fri: 9:00 AM - 5:00 PM SAST</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm p-6 rounded-lg border border-bluemp-400/20">
                <h3 className="heading-3 text-bluemp-400 mb-4">What to Expect</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle size={16} className="text-bluemp-400 mt-1 flex-shrink-0" />
                    <span className="body-small text-gray-300">30-minute discovery conversation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={16} className="text-bluemp-400 mt-1 flex-shrink-0" />
                    <span className="body-small text-gray-300">Discussion of your AI goals and challenges</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={16} className="text-bluemp-400 mt-1 flex-shrink-0" />
                    <span className="body-small text-gray-300">High-level solution recommendations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle size={16} className="text-bluemp-400 mt-1 flex-shrink-0" />
                    <span className="body-small text-gray-300">Next steps and timeline discussion</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Discovery Call Form */}
            <div className="lg:col-span-3">
              <div className="bg-gray-900/30 backdrop-blur-sm p-8 rounded-lg border border-bluemp-400/20">
                <h2 className="heading-2 text-bluemp-400 mb-6">Book Your Discovery Call</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block body-medium text-white mb-2">First Name *</label>
                      <Input 
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="bg-gray-900/50 border-bluemp-400/30 text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block body-medium text-white mb-2">Last Name *</label>
                      <Input 
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="bg-gray-900/50 border-bluemp-400/30 text-white"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block body-medium text-white mb-2">Email *</label>
                      <Input 
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="bg-gray-900/50 border-bluemp-400/30 text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block body-medium text-white mb-2">Phone</label>
                      <Input 
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="bg-gray-900/50 border-bluemp-400/30 text-white"
                      />
                    </div>
                  </div>

                  {/* Company Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block body-medium text-white mb-2">Company *</label>
                      <Input 
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        className="bg-gray-900/50 border-bluemp-400/30 text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block body-medium text-white mb-2">Industry *</label>
                      <Select value={formData.industry} onValueChange={(value) => handleInputChange('industry', value)}>
                        <SelectTrigger className="bg-gray-900/50 border-bluemp-400/30 text-white">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="financial-services">Financial Services</SelectItem>
                          <SelectItem value="insurance">Insurance</SelectItem>
                          <SelectItem value="banking">Banking</SelectItem>
                          <SelectItem value="investment">Investment Management</SelectItem>
                          <SelectItem value="communications">Communications</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block body-medium text-white mb-2">Team Size</label>
                      <Select value={formData.teamSize} onValueChange={(value) => handleInputChange('teamSize', value)}>
                        <SelectTrigger className="bg-gray-900/50 border-bluemp-400/30 text-white">
                          <SelectValue placeholder="Select team size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-10">1-10 employees</SelectItem>
                          <SelectItem value="11-50">11-50 employees</SelectItem>
                          <SelectItem value="51-200">51-200 employees</SelectItem>
                          <SelectItem value="201-1000">201-1000 employees</SelectItem>
                          <SelectItem value="1000+">1000+ employees</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block body-medium text-white mb-2">AI Experience Level</label>
                      <Select value={formData.aiExperience} onValueChange={(value) => handleInputChange('aiExperience', value)}>
                        <SelectTrigger className="bg-gray-900/50 border-bluemp-400/30 text-white">
                          <SelectValue placeholder="Select experience" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                          <SelectItem value="expert">Expert</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Project Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block body-medium text-white mb-2">Project Type *</label>
                      <Select value={formData.projectType} onValueChange={(value) => handleInputChange('projectType', value)}>
                        <SelectTrigger className="bg-gray-900/50 border-bluemp-400/30 text-white">
                          <SelectValue placeholder="Select project type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="consulting">AI Consulting & Strategy</SelectItem>
                          <SelectItem value="contactability">Contactability Solution</SelectItem>
                          <SelectItem value="broker-analytics">Broker Analytics</SelectItem>
                          <SelectItem value="campaigns">Campaign Solutions</SelectItem>
                          <SelectItem value="unclaimed-funds">Unclaimed Funds</SelectItem>
                          <SelectItem value="learning">AI Learning & Training</SelectItem>
                          <SelectItem value="custom">Custom Solution</SelectItem>
                          <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block body-medium text-white mb-2">Timeline</label>
                      <Select value={formData.timeline} onValueChange={(value) => handleInputChange('timeline', value)}>
                        <SelectTrigger className="bg-gray-900/50 border-bluemp-400/30 text-white">
                          <SelectValue placeholder="Select timeline" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">Immediate (&lt; 1 month)</SelectItem>
                          <SelectItem value="short">Short-term (1-3 months)</SelectItem>
                          <SelectItem value="medium">Medium-term (3-6 months)</SelectItem>
                          <SelectItem value="long">Long-term (6+ months)</SelectItem>
                          <SelectItem value="exploring">Just Exploring</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block body-medium text-white mb-2">Budget Range</label>
                    <Select value={formData.budget} onValueChange={(value) => handleInputChange('budget', value)}>
                      <SelectTrigger className="bg-gray-900/50 border-bluemp-400/30 text-white">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-50k">Under R50,000</SelectItem>
                        <SelectItem value="50k-100k">R50,000 - R100,000</SelectItem>
                        <SelectItem value="100k-500k">R100,000 - R500,000</SelectItem>
                        <SelectItem value="500k-1m">R500,000 - R1,000,000</SelectItem>
                        <SelectItem value="over-1m">Over R1,000,000</SelectItem>
                        <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block body-medium text-white mb-2">Current AI Usage</label>
                    <Textarea 
                      value={formData.currentAI}
                      onChange={(e) => handleInputChange('currentAI', e.target.value)}
                      placeholder="Describe any current AI tools or initiatives..."
                      className="bg-gray-900/50 border-bluemp-400/30 text-white min-h-[80px]"
                    />
                  </div>

                  <div>
                    <label className="block body-medium text-white mb-2">Primary Challenges *</label>
                    <Textarea 
                      value={formData.challenges}
                      onChange={(e) => handleInputChange('challenges', e.target.value)}
                      placeholder="What are your main business challenges that AI could help solve?"
                      className="bg-gray-900/50 border-bluemp-400/30 text-white min-h-[100px]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block body-medium text-white mb-2">Goals & Objectives *</label>
                    <Textarea 
                      value={formData.goals}
                      onChange={(e) => handleInputChange('goals', e.target.value)}
                      placeholder="What do you hope to achieve with AI implementation?"
                      className="bg-gray-900/50 border-bluemp-400/30 text-white min-h-[100px]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block body-medium text-white mb-2">Additional Information</label>
                    <Textarea 
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Any additional information you'd like to share..."
                      className="bg-gray-900/50 border-bluemp-400/30 text-white min-h-[100px]"
                    />
                  </div>

                  <Button type="submit" className="btn-primary w-full">
                    Book Discovery Call
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* End of contact form section */}
    </div>
  );
};

export default Contact;